import {useCallback, useEffect, useState} from 'react';
import {Box, Card, Grid, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import '../settings.professional-caregiver.screen.scss';
import './institution-settings.professional-caregiver.screen.scss';
import {translations} from '../../../../../../i18n/translations';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import Switch from '../../../../../../components/switch/switch';
import {useAuth} from '../../../../../../contexts/auth/context';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {ScreenPaths} from '../../../../../../enums';
import {EntitiesRepository} from '../../../../../../repositories/entities';
import {PatchEntitySettings} from '../../../../../../backend/requests/patch-entity-settings';
import {EntitySettings} from '../../../../../../backend/responses/entity-settings';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';

export default function InstitutionSettingsProfessionalCaregiverScreen() {
  const {t} = useTranslation();
  const {state: authState, hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Settings_Entity_Settings);

  const snackBarContext = useSnackBar();
  const entityId = authState.session?.selectedAffiliation?.entity.id;

  const [settings, setSettings] = useState<EntitySettings | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!entityId) {
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    EntitiesRepository.instance
      .getEntitySettings(entityId)
      .then((response) => {
        if (!isCancelled) {
          setSettings(response);
        }
      })
      .catch((e) => {
        console.error(e);
        if (!isCancelled) {
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.institution_settings_screen.load_error_toast),
          });
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const updateSettings = useCallback(
    async (body: PatchEntitySettings) => {
      if (!entityId) {
        return;
      }

      setIsUpdating(true);
      try {
        setSettings(await EntitiesRepository.instance.updateEntitySettings(entityId, body));
      } catch (e) {
        console.error(e);
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.institution_settings_screen.update_error_toast),
        });
      } finally {
        setIsUpdating(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityId]
  );

  const settingsUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}`;

  return (
    <Box className="settings-menu-wrapper">
      <GenericHeader
        titleCrumbs={[
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .settings
            ),
            link: settingsUrl,
          },
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .institution
            ),
            link: `${settingsUrl}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings}`,
          },
          {
            label: t(translations.screen_paths.institution_settings_settings),
            link: `${settingsUrl}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Settings}`,
          },
        ]}
        isLoading={false}
        leftColumn={
          <Typography className="settings-menu-header-title" noWrap>
            {canWrite
              ? t(translations.institution_settings_screen.subtitle.write)
              : t(translations.institution_settings_screen.subtitle.read)}
          </Typography>
        }
      />
      <Box>
        <Card className="settings-menu-card" elevation={0}>
          <Box className="institution-settings-list">
            <Grid container className="institution-settings-list-header">
              <Grid item xs={9}>
                <Typography
                  className="institution-settings-list-header-label"
                  sx={{fontWeight: 700}}
                >
                  {t(translations.institution_settings_screen.columns.setting)}
                </Typography>
              </Grid>
              <Grid item xs={3} className="institution-settings-list-action-cell">
                <Typography
                  className="institution-settings-list-header-label"
                  sx={{fontWeight: 700}}
                >
                  {t(translations.institution_settings_screen.columns.active)}
                </Typography>
              </Grid>
            </Grid>

            <Grid container className="institution-settings-list-row">
              <Grid item xs={9}>
                <Typography className="institution-settings-list-row-label">
                  {t(translations.institution_settings_screen.attendance_tracker)}
                </Typography>
              </Grid>
              <Grid item xs={3} className="institution-settings-list-action-cell">
                <Switch
                  aria-label={t(translations.institution_settings_screen.attendance_tracker)}
                  checked={Boolean(settings?.isAttendanceTrackerEnabled)}
                  value={Boolean(settings?.isAttendanceTrackerEnabled)}
                  disabled={!canWrite || isLoading || isUpdating || !settings}
                  onChange={(event) =>
                    updateSettings({isAttendanceTrackerEnabled: event.target.checked})
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
